import React, { useEffect, useRef } from "react";

export function decodeThreeDsHtml(value) {
  const raw = String(value || "");
  if (!raw) return "";

  // Wompi Panama returns BROWSER_INFO/FINGERPRINT method HTML as an
  // entity-encoded document. Decode only when the document itself starts
  // encoded; do not reinterpret already-literal HTML merely because it
  // contains &quot; or another entity inside an attribute.
  const trimmed = raw.trimStart();
  const encodedDocument =
    /^(?:&lt;|&#0*60;|&#x0*3c;)/i.test(trimmed);

  if (!encodedDocument) return raw;

  try {
    const doc = new DOMParser().parseFromString(raw, "text/html");
    return doc.documentElement?.textContent || raw;
  } catch (_) {
    return raw;
  }
}

export function WompiThreeDsFrame({
  title,
  className,
  html,
  hiddenExecution = false,
}) {
  const frameRef = useRef(null);

  useEffect(() => {
    const frame = frameRef.current;
    if (!frame || !html) return undefined;

    let active = true;
    let written = false;
    let timerId = null;

    const writeProviderDocument = () => {
      if (!active || written) return;

      try {
        const doc =
          frame.contentDocument ||
          frame.contentWindow?.document;

        if (!doc) return;

        written = true;

        // WKWebView has historically been less predictable with dynamically
        // updated iframe.srcdoc. Writing into an explicit about:blank frame
        // gives the provider document a normal parse/load lifecycle so its
        // BROWSER_INFO auto-submit script can execute.
        doc.open();
        doc.write(html);
        doc.close();
      } catch (_) {
        // Safe browser fallback. This does not create a Loop/Wompi financial
        // request; it only restores the previous iframe rendering mechanism.
        frame.srcdoc = html;
      }
    };

    const onInitialFrameLoad = () => {
      writeProviderDocument();
    };

    frame.addEventListener("load", onInitialFrameLoad);
    timerId = window.setTimeout(writeProviderDocument, 0);

    return () => {
      active = false;
      frame.removeEventListener("load", onInitialFrameLoad);
      if (timerId) {
        window.clearTimeout(timerId);
      }
    };
  }, [html]);

  const hiddenStyle = hiddenExecution
    ? {
        width: "1px",
        height: "1px",
        border: 0,
        position: "fixed",
        left: 0,
        top: 0,
        opacity: 0.01,
        pointerEvents: "none",
      }
    : undefined;

  return (
    <iframe
      ref={frameRef}
      title={title}
      className={className}
      src="about:blank"
      style={hiddenStyle}
      aria-hidden={hiddenExecution ? "true" : undefined}
    />
  );
}

